import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect, PageHeader } from "@/components/common/Page";
import { EmptyState, ErrorState, LoadingState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { UserAvatar } from "@/components/common/PhotoUpload";
import { StudentFormDialog } from "@/components/students/StudentFormDialog";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { BILLING } from "@/lib/format";

export default function Students() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const { data, loading, error, reload } = useFetch(`/students?status=${status}&q=${encodeURIComponent(q)}`);
  const max = user.plan?.max_students;
  const activeCount = (data || []).filter((s) => s.status === "ativo").length;

  return (
    <div className="animate-in fade-in duration-300">
      <PageHeader title="Alunos" subtitle={max ? `Plano ${user.plan.name}: até ${max} alunos ativos` : "Alunos ilimitados no seu plano"}
        actions={<Button onClick={() => setOpen(true)} data-testid="new-student-btn"><Plus className="mr-2 h-4 w-4" />Novo aluno</Button>} />
      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Buscar aluno pelo nome" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="students-search-input" />
        </div>
        <NativeSelect className="sm:w-44" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="students-status-filter">
          <option value="">Todos</option>
          <option value="ativo">Ativos</option>
          <option value="inativo">Inativos</option>
        </NativeSelect>
      </div>
      {error ? <ErrorState error={error} onRetry={reload} /> : loading && !data ? <LoadingState /> : data.length === 0 ? (
        <EmptyState icon={Users} title={q || status ? "Nenhum aluno encontrado" : "Nenhum aluno cadastrado ainda."}
          description={q || status ? "Tente outro filtro." : "Cadastre seu primeiro aluno para começar a agendar aulas."} testId="students-empty"
          action={!q && !status && <Button onClick={() => setOpen(true)} data-testid="add-first-student-btn">Adicionar primeiro aluno</Button>} />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {data.map((s) => (
              <Link key={s.id} to={`/alunos/${s.id}`} className="group flex items-center gap-4 rounded-xl border bg-card p-4 transition-all hover:-translate-y-0.5 hover:shadow-md" data-testid={`student-card-${s.id}`}>
                <UserAvatar path={s.photo_path} name={s.name} size="h-12 w-12" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold group-hover:text-primary">{s.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{(s.subjects || []).join(", ") || "Sem disciplinas"} · {BILLING[s.billing_type]}</p>
                </div>
                <StatusBadge status={s.status} />
              </Link>
            ))}
          </div>
          {max && <p className="mt-4 text-xs text-muted-foreground" data-testid="students-usage">{activeCount} de {max} alunos ativos utilizados</p>}
        </>
      )}
      <StudentFormDialog open={open} onOpenChange={setOpen} onSaved={reload} />
    </div>
  );
}
